import { createRoot } from 'react-dom/client'

import { CacheProvider } from '@emotion/react'
import { AgentStatus } from '@oneToOne/agentStatus/components/AgentStatus'
import type { AgentStatusParams } from '@oneToOne/agentStatus/params'
import { TargetProvider } from '@oneToOne/shared/contexts/TargetContext'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import ErrorBoundary from '@core/components/ErrorBoundary'
import { getSentryConfig, logError } from '@core/logging'
import { getLanguage } from '@core/params/helpers/getLanguage'
import { ensurePolyfills } from '@core/polyfills'
import { createEmbedEmotionCache } from '@core/styles/createEmbedEmotionCache'

interface AgentRenderProps {
  target: ShadowRoot
  widgetId: string
  widgetAttributes: Record<string, string>
}

/**
 * A render function for rendering the agent status widget.
 */
export function agentStatusRender({
  target,
  widgetId,
  widgetAttributes,
}: AgentRenderProps) {
  const { chat_channel_id } = widgetAttributes
  const widget_id = widgetId

  const initialParams: AgentStatusParams = {
    type: 'agent-status',
    widget_id,
    lang: getLanguage(),
  }

  ensurePolyfills(() => {
    const sentryConfig = getSentryConfig()
    sentryConfig.addTag('fwn_chat_channel_id', chat_channel_id ?? '')
    sentryConfig.addTag('fwn_context', 'agent-status')

    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
        },
      },
    })

    try {
      if (target) {
        const root = createRoot(target)
        root.unmount()
        root.render(
          <ErrorBoundary onError={(error) => logError(error)}>
            <CacheProvider
              value={createEmbedEmotionCache({
                // Do not turn on speedy mode. It will break the rrweb snapshot.
                speedy: false,
                container: target,
              })}
            >
              <QueryClientProvider client={queryClient}>
                <TargetProvider target={target}>
                  <AgentStatus
                    initialParams={initialParams}
                    widgetAttributes={widgetAttributes}
                  />
                </TargetProvider>
                <ReactQueryDevtools />
              </QueryClientProvider>
            </CacheProvider>
          </ErrorBoundary>,
        )
      }
    } catch (error) {
      logError(error)
    }
  })
}
